import { legacyHttp } from '@noor/api';
import {
  merchantChatListSchema,
  merchantChatSchema,
  merchantChatThreadSchema,
  type MerchantChat,
  type MerchantChatList,
  type MerchantChatThread,
} from '../model/schema';

/** List filter, as the old drawer built it (status has no UI — always all). */
export interface MerchantChatsQuery {
  skip: number;
  limit: number;
  /** Free-text search over the username; omitted when empty. */
  username?: string | undefined;
}

export interface MerchantThreadQuery {
  skip: number;
  limit: number;
}

export interface SendMerchantMessageInput {
  message: string;
  files: File[];
}

/**
 * The legacy chat client. Responses are un enveloped DTOs (`{data, count, …}`),
 * parsed at the boundary; sends are multipart `FormData`, which passes through
 * `ofetch` untouched (see `vendor-info-api.topUpInvoice`).
 */
export const merchantChatApi = {
  async list(query: MerchantChatsQuery): Promise<MerchantChatList> {
    const raw = await legacyHttp.get<unknown>('chats', {
      skip: query.skip,
      limit: query.limit,
      ...(query.username ? { username: query.username } : {}),
    });
    return merchantChatListSchema.parse(raw);
  },

  async thread(chatId: number, query: MerchantThreadQuery): Promise<MerchantChatThread> {
    const raw = await legacyHttp.get<unknown>(`chats/${chatId}/messages`, {
      skip: query.skip,
      limit: query.limit,
    });
    return merchantChatThreadSchema.parse(raw);
  },

  async send(chatId: number, input: SendMerchantMessageInput): Promise<unknown> {
    const formData = new FormData();
    // The backend requires a non-empty message: attachments-only sends carry
    // the old client's `img` placeholder, exactly as it did.
    formData.append('message', input.message || 'img');
    for (const file of input.files) formData.append('files', file);
    return legacyHttp.post(`chats/${chatId}/messages`, formData);
  },

  /** A new thread; the caller sends the first message into the returned id. */
  async create(subject: string): Promise<MerchantChat> {
    const raw = await legacyHttp.post<unknown>('chats', {
      subject,
      role: 'merchant',
    });
    return merchantChatSchema.parse(raw);
  },
};
